/**
 * Nutrition Calculations Utility
 *
 * Contains all calculation functions for nutrition planning:
 * - BMR (Basal Metabolic Rate) calculations
 * - TDEE (Total Daily Energy Expenditure)
 * - Macro distribution
 * - Body composition
 * - Meal planning
 * - Compliance tracking
 */

// Activity levels with multipliers
export const activityLevels = [
  { value: 'sedentary', label: 'Sedentary (little or no exercise)', multiplier: 1.2 },
  { value: 'light', label: 'Lightly active (1-3 days/week)', multiplier: 1.375 },
  { value: 'moderate', label: 'Moderately active (3-5 days/week)', multiplier: 1.55 },
  { value: 'active', label: 'Very active (6-7 days/week)', multiplier: 1.725 },
  { value: 'veryActive', label: 'Extra active (very hard exercise)', multiplier: 1.9 },
  { value: 'athlete', label: 'Athlete (professional training)', multiplier: 2.2 }
];

// Nutrition goals
export const nutritionGoals = [
  { value: 'lose', label: 'Lose Weight', calorieAdjustment: -500, weeklyChange: -500 },
  { value: 'maintain', label: 'Maintain Weight', calorieAdjustment: 0, weeklyChange: 0 },
  { value: 'gain', label: 'Gain Weight', calorieAdjustment: 500, weeklyChange: 500 }
];

// Meal timing options
export const mealTimes = [
  { meal: 'Breakfast', defaultTime: '07:00' },
  { meal: 'Morning Snack', defaultTime: '10:00' },
  { meal: 'Lunch', defaultTime: '12:30' },
  { meal: 'Afternoon Snack', defaultTime: '15:30' },
  { meal: 'Dinner', defaultTime: '18:30' },
  { meal: 'Evening Snack', defaultTime: '21:00' }
];

/**
 * Calculate BMR using Katch-McArdle Formula (most accurate with body fat %)
 * BMR = 370 + (21.6 × Lean Body Mass in kg)
 *
 * @param {number} weight - Body weight in kg
 * @param {number} bodyFatPercentage - Body fat percentage (e.g., 15 for 15%)
 * @returns {number} BMR in calories/day
 */
export const calculateBMR = (weight, bodyFatPercentage) => {
  const fatMass = weight * (bodyFatPercentage / 100);
  const leanMass = weight - fatMass;
  return 370 + (21.6 * leanMass);
};

/**
 * Calculate BMR using Mifflin-St Jeor Formula (when body fat % is unknown)
 *
 * @param {number} weight - Body weight in kg
 * @param {number} height - Height in cm
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {number} BMR in calories/day
 */
export const calculateBMRMifflinStJeor = (weight, height, age, gender) => {
  if (gender === 'male') {
    return (10 * weight) + (6.25 * height) - (5 * age) + 5;
  } else {
    return (10 * weight) + (6.25 * height) - (5 * age) - 161;
  }
};

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 *
 * @param {number} bmr - Basal Metabolic Rate
 * @param {number} activityMultiplier - Activity level multiplier (1.2 - 1.9)
 * @param {number} exerciseCalories - Additional exercise calories (optional)
 * @returns {number} TDEE in calories/day
 */
export const calculateTDEE = (bmr, activityMultiplier, exerciseCalories = 0) => {
  return (bmr * activityMultiplier) + exerciseCalories;
};

/**
 * Calculate exercise calories burned using METs
 * Calories = METs × weight in kg × time in hours
 *
 * @param {Object} exercise - Exercise object with metsValue
 * @param {number} weightKg - Body weight in kg
 * @param {number} durationMinutes - Duration in minutes
 * @returns {number} Calories burned
 */
export const calculateExerciseCalories = (exercise, weightKg, durationMinutes) => {
  const hours = durationMinutes / 60;
  return exercise.metsValue * weightKg * hours;
};

/**
 * Calculate macros based on goal and body composition
 *
 * @param {number} dailyCalories - Target daily calories
 * @param {string} goal - 'lose', 'maintain', or 'gain'
 * @param {number} bodyWeight - Body weight in kg
 * @param {number} leanMass - Lean body mass in kg
 * @returns {Object} Macro distribution with grams, calories, and percentages
 */
export const calculateMacros = (dailyCalories, goal, bodyWeight, leanMass) => {
  let proteinGrams, carbGrams, fatGrams;

  switch(goal) {
    case 'lose':
      // Higher protein to preserve muscle during cut
      proteinGrams = leanMass * 2.5; // 2.5g per kg of lean mass
      fatGrams = bodyWeight * 0.7; // 0.7g per kg body weight
      carbGrams = (dailyCalories - (proteinGrams * 4) - (fatGrams * 9)) / 4;
      break;

    case 'maintain':
      // Balanced macros
      proteinGrams = bodyWeight * 2.0; // 2g per kg body weight
      fatGrams = bodyWeight * 1.0; // 1g per kg body weight
      carbGrams = (dailyCalories - (proteinGrams * 4) - (fatGrams * 9)) / 4;
      break;

    case 'gain':
      // Higher carbs for energy and growth
      proteinGrams = bodyWeight * 2.2; // 2.2g per kg body weight
      fatGrams = bodyWeight * 1.2; // 1.2g per kg body weight
      carbGrams = (dailyCalories - (proteinGrams * 4) - (fatGrams * 9)) / 4;
      break;

    default:
      // Default to maintenance
      proteinGrams = bodyWeight * 2.0;
      fatGrams = bodyWeight * 1.0;
      carbGrams = (dailyCalories - (proteinGrams * 4) - (fatGrams * 9)) / 4;
  }

  // Calculate percentages
  const proteinCalories = proteinGrams * 4;
  const carbCalories = carbGrams * 4;
  const fatCalories = fatGrams * 9;
  const totalCalories = proteinCalories + carbCalories + fatCalories;

  return {
    calories: Math.round(totalCalories),
    protein: {
      grams: Math.round(proteinGrams),
      calories: Math.round(proteinCalories),
      percentage: Math.round((proteinCalories / totalCalories) * 100)
    },
    carbs: {
      grams: Math.round(carbGrams),
      calories: Math.round(carbCalories),
      percentage: Math.round((carbCalories / totalCalories) * 100)
    },
    fat: {
      grams: Math.round(fatGrams),
      calories: Math.round(fatCalories),
      percentage: Math.round((fatCalories / totalCalories) * 100)
    }
  };
};

/**
 * Calculate body fat percentage using US Navy Method
 *
 * @param {string} gender - 'male' or 'female'
 * @param {number} waist - Waist circumference in cm
 * @param {number} neck - Neck circumference in cm
 * @param {number} height - Height in cm
 * @param {number} hips - Hip circumference in cm (required for female)
 * @returns {number} Body fat percentage
 */
export const calculateBodyFatNavy = (gender, waist, neck, height, hips = null) => {
  if (gender === 'male') {
    // Men: BF% = 86.010 × log10(waist - neck) - 70.041 × log10(height) + 36.76
    return 86.010 * Math.log10(waist - neck) - 70.041 * Math.log10(height) + 36.76;
  } else {
    // Women: BF% = 163.205 × log10(waist + hips - neck) - 97.684 × log10(height) - 78.387
    if (!hips) throw new Error('Hip measurement required for female body fat calculation');
    return 163.205 * Math.log10(waist + hips - neck) - 97.684 * Math.log10(height) - 78.387;
  }
};

/**
 * Calculate meal targets based on daily targets and meal distribution
 *
 * @param {Object} dailyMacros - Daily macro targets
 * @param {number} mealsPerDay - Number of meals per day (3-6)
 * @param {number} mealNumber - Meal number (1-6)
 * @returns {Object} Meal-specific macro targets
 */
export const calculateMealTargets = (dailyMacros, mealsPerDay, mealNumber) => {
  // Default meal distribution percentages
  const distributions = {
    3: [35, 35, 30], // Breakfast, Lunch, Dinner
    4: [25, 35, 30, 10], // + Snack
    5: [25, 10, 30, 25, 10], // + 2 Snacks
    6: [20, 10, 25, 10, 25, 10] // 3 main meals + 3 snacks
  };

  const distribution = distributions[mealsPerDay] || distributions[3];
  const percentage = distribution[mealNumber - 1] / 100;

  return {
    calories: Math.round(dailyMacros.calories * percentage),
    protein: Math.round(dailyMacros.protein.grams * percentage),
    carbs: Math.round(dailyMacros.carbs.grams * percentage),
    fat: Math.round(dailyMacros.fat.grams * percentage)
  };
};

/**
 * Calculate shopping list quantities based on meal plan
 *
 * @param {Array} mealPlan - Array of meals with foods
 * @param {number} days - Number of days to plan for
 * @returns {Array} Shopping list with aggregated quantities
 */
export const calculateShoppingList = (mealPlan, days = 7) => {
  const shoppingList = {};

  mealPlan.forEach(meal => {
    meal.foods.forEach(item => {
      const foodName = item.food.name;
      const quantity = item.quantity * days;

      if (shoppingList[foodName]) {
        shoppingList[foodName] += quantity;
      } else {
        shoppingList[foodName] = quantity;
      }
    });
  });

  // Convert to array and round quantities
  return Object.entries(shoppingList).map(([name, quantity]) => ({
    name,
    quantity: Math.round(quantity),
    unit: 'g',
    quantityKg: (quantity / 1000).toFixed(2)
  }));
};

/**
 * Calculate compliance score based on logged vs planned nutrition
 *
 * @param {Object} logged - Logged nutrition data
 * @param {Object} planned - Planned nutrition targets
 * @returns {Object} Compliance scores for each macro and overall
 */
export const calculateCompliance = (logged, planned) => {
  const calorieCompliance = 100 - Math.abs(((logged.calories - planned.calories) / planned.calories) * 100);
  const proteinCompliance = 100 - Math.abs(((logged.protein - planned.protein) / planned.protein) * 100);
  const carbCompliance = 100 - Math.abs(((logged.carbs - planned.carbs) / planned.carbs) * 100);
  const fatCompliance = 100 - Math.abs(((logged.fat - planned.fat) / planned.fat) * 100);

  // Weight average: Protein 35%, Calories 30%, Carbs 20%, Fat 15%
  const overallCompliance = (
    (proteinCompliance * 0.35) +
    (calorieCompliance * 0.30) +
    (carbCompliance * 0.20) +
    (fatCompliance * 0.15)
  );

  return {
    overall: Math.max(0, Math.min(100, overallCompliance)),
    calories: Math.max(0, Math.min(100, calorieCompliance)),
    protein: Math.max(0, Math.min(100, proteinCompliance)),
    carbs: Math.max(0, Math.min(100, carbCompliance)),
    fat: Math.max(0, Math.min(100, fatCompliance))
  };
};

/**
 * Project weight loss/gain based on caloric deficit/surplus
 *
 * @param {number} currentWeight - Current weight in kg
 * @param {number} weeklyCalorieBalance - Weekly calorie balance (deficit or surplus)
 * @param {number} weeks - Number of weeks to project
 * @returns {number} Projected weight in kg
 */
export const projectWeight = (currentWeight, weeklyCalorieBalance, weeks) => {
  // 7700 calories = 1kg of body weight change
  const weeklyWeightChange = weeklyCalorieBalance / 7700;
  return currentWeight + (weeklyWeightChange * weeks);
};

/**
 * Calculate daily calorie target based on goal
 *
 * @param {number} tdee - Total Daily Energy Expenditure
 * @param {string} goal - 'lose', 'maintain', or 'gain'
 * @returns {number} Daily calorie target
 */
export const calculateDailyCalorieTarget = (tdee, goal) => {
  const goalData = nutritionGoals.find(g => g.value === goal);
  if (!goalData) return tdee;

  return Math.round(tdee + goalData.calorieAdjustment);
};

// Export all for convenience
export default {
  activityLevels,
  nutritionGoals,
  mealTimes,
  calculateBMR,
  calculateBMRMifflinStJeor,
  calculateTDEE,
  calculateExerciseCalories,
  calculateMacros,
  calculateBodyFatNavy,
  calculateMealTargets,
  calculateShoppingList,
  calculateCompliance,
  projectWeight,
  calculateDailyCalorieTarget
};
